import React, { useState, useEffect, useRef } from "react";
import { getTokenInternal } from "./useToken";


const EditItemForm = ({ itemData, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: "",
    types: "",
    event: "",
    maps: "",
    maps_name: "",
    url: "",
    games_id: "",
    acquisition_method_id: "",
    is_active: false,
    is_new: false,
    private_testing: false,
  });

  const [eventCreated, setEventCreated] = useState(false);
  const [token, setToken] = useState(null);
  const [acquisitionMethodOptions, setAcquisitionMethodOptions] = useState([]);
  const [unlockRequirementOptions, setUnlockRequirementOptions] = useState([]);
  const [linkedRequirementIds, setLinkedRequirementIds] = useState(new Set());
  const originalLinkedIds = useRef(new Set());

  useEffect(() => {
    if (itemData) {
      setFormData({
        name: itemData.name || "",
        types: itemData.types || "",
        event: itemData.event || "",
        maps: itemData.maps || "",
        maps_name: itemData.maps_name || "",
        url: itemData.url || "",
        games_id: itemData.games_id || "",
        acquisition_method_id: itemData.acquisition_method_id || "",
        is_active: itemData.is_active || false,
        is_new: itemData.is_new || false,
        private_testing: itemData.private_testing || false,
      });

      if (itemData.games_id) {
        fetch(`${process.env.REACT_APP_API_HOST}/api/unlock-requirements?games_id=${itemData.games_id}`)
          .then(r => r.ok ? r.json() : [])
          .then(setUnlockRequirementOptions);

        fetch(`${process.env.REACT_APP_API_HOST}/api/unlock-requirements/by-item?games_id=${itemData.games_id}`)
          .then(r => r.ok ? r.json() : {})
          .then(data => {
            const ids = new Set(data[String(itemData.id)] || []);
            originalLinkedIds.current = new Set(ids);
            setLinkedRequirementIds(new Set(ids));
          });
      }
    }
  }, [itemData]);

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const fetchedToken = await getTokenInternal();
        setToken(fetchedToken);
      } catch (error) {
        console.error("Error fetching token:", error);
      }
    };

    const fetchAcquisitionMethods = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_HOST}/api/acquisition-methods`);
        if (res.ok) setAcquisitionMethodOptions(await res.json());
      } catch (error) {
        console.error("Error fetching acquisition methods:", error);
      }
    };

    fetchToken();
    fetchAcquisitionMethods();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const toggleRequirement = (reqId) => {
    setLinkedRequirementIds(prev => {
      const next = new Set(prev);
      if (next.has(reqId)) next.delete(reqId); else next.add(reqId);
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...formData,
        games_id: formData.games_id ? parseInt(formData.games_id) : null,
        acquisition_method_id: formData.acquisition_method_id
          ? parseInt(formData.acquisition_method_id)
          : null,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/items/${itemData.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token.access_token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        const toLink = [...linkedRequirementIds].filter(id => !originalLinkedIds.current.has(id));
        const toUnlink = [...originalLinkedIds.current].filter(id => !linkedRequirementIds.has(id));
        await Promise.all([
          ...toLink.map(id => fetch(
            `${process.env.REACT_APP_API_HOST}/api/unlock-requirements/${id}/link-item/${itemData.id}`,
            { method: "POST", headers: { Authorization: `Bearer ${token.access_token}` } }
          )),
          ...toUnlink.map(id => fetch(
            `${process.env.REACT_APP_API_HOST}/api/unlock-requirements/${id}/link-item/${itemData.id}`,
            { method: "DELETE", headers: { Authorization: `Bearer ${token.access_token}` } }
          )),
        ]);

        setEventCreated(true);
        onSave();
      } else {
        console.error("Failed to update item");
      }
    } catch (error) {
      console.error("Error updating item:", error);
    }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm("Are you sure you want to delete this item?");
    if (!confirmed) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/items/${itemData.id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token.access_token}`,
          },
        }
      );

      if (response.ok) {
        onClose();
      } else {
        console.error("Failed to delete item:", response.statusText);
      }
    } catch (error) {
      console.error("Error deleting item:", error);
    }
  };

  return (
    <div className="text-black">
      <h2 className="text-xl font-bold mb-4">Edit Item</h2>
      {eventCreated && (
        <p className="text-green-600 text-center mb-2">Item Updated!</p>
      )}
      <form onSubmit={handleSubmit} className="max-w-md mx-auto my-2">
        <label className="block font-bold mb-1">
          Name:
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="mt-1 p-2 w-full border rounded"
          />
        </label>
        <label className="block font-bold mb-1 mt-3">
          Category:
          <input
            type="text"
            name="types"
            value={formData.types}
            onChange={handleChange}
            className="mt-1 p-2 w-full border rounded"
          />
        </label>
        <label className="block font-bold mb-1 mt-3">
          Event:
          <input
            type="text"
            name="event"
            value={formData.event}
            onChange={handleChange}
            className="mt-1 p-2 w-full border rounded"
          />
        </label>
        <label className="block font-bold mb-1 mt-3">
          Maps Exclusive:
          <input
            type="text"
            name="maps"
            value={formData.maps}
            onChange={handleChange}
            className="mt-1 p-2 w-full border rounded"
          />
        </label>
        <label className="block font-bold mb-1 mt-3">
          Maps Name:
          <input
            type="text"
            name="maps_name"
            value={formData.maps_name}
            onChange={handleChange}
            className="mt-1 p-2 w-full border rounded"
          />
        </label>
        <label className="block font-bold mb-1 mt-3">
          URL:
          <input
            type="text"
            name="url"
            value={formData.url}
            onChange={handleChange}
            className="mt-1 p-2 w-full border rounded"
          />
        </label>
        <label className="block font-bold mb-1 mt-3">
          Acquisition Method:
          <select
            name="acquisition_method_id"
            value={formData.acquisition_method_id}
            onChange={handleChange}
            className="mt-1 p-2 w-full border rounded"
          >
            <option value="">None / unspecified</option>
            {acquisitionMethodOptions.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </label>
        {unlockRequirementOptions.length > 0 && (
          <div className="mt-3">
            <label className="block font-bold mb-1">Unlock Requirements:</label>
            <div className="space-y-1 max-h-40 overflow-y-auto border rounded p-2 bg-gray-50">
              {unlockRequirementOptions.map(req => (
                <label key={req.id} className="flex items-center gap-2 cursor-pointer text-sm font-normal">
                  <input
                    type="checkbox"
                    checked={linkedRequirementIds.has(req.id)}
                    onChange={() => toggleRequirement(req.id)}
                    className="w-4 h-4"
                  />
                  {req.name}{req.platform ? ` (${req.platform})` : ""}
                </label>
              ))}
            </div>
          </div>
        )}

        <input
          type="hidden"
          name="games_id"
          value={formData.games_id}
          onChange={handleChange}
        />

        <div className="flex gap-6 mt-4">
          <label className="flex items-center gap-2 font-bold cursor-pointer">
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              className="w-4 h-4"
            />
            Active (visible to players)
          </label>
          <label className="flex items-center gap-2 font-bold cursor-pointer">
            <input
              type="checkbox"
              name="is_new"
              checked={formData.is_new}
              onChange={handleChange}
              className="w-4 h-4"
            />
            New this season
          </label>
          <label className="flex items-center gap-2 font-bold cursor-pointer">
            <input
              type="checkbox"
              name="private_testing"
              checked={formData.private_testing}
              onChange={handleChange}
              className="w-4 h-4"
            />
            Private testing only
          </label>
        </div>

        <div className="flex justify-between mt-6">
          <button
            type="submit"
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-800"
          >
            Save
          </button>
          <button
            type="button"
            onClick={onClose}
            className="bg-gray-400 text-black px-4 py-2 rounded hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDelete}
            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-800"
          >
            Delete
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditItemForm;
